import { Card, Divider, Skeleton, Statistic, Tag, Tooltip, Typography } from "antd";
import { FileQuestion, Shuffle, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetExamQuestionSummary } from "@hooks";

interface Props {
  examId?: string;
}

// Maps the backend QuestionTypeEnum InternalId to a localized label key.
const questionTypeLabelKey: Record<number, string> = {
  1: "pages.bankPool.shortAnswer",
  2: "pages.bankPool.numeric",
  3: "pages.bankPool.singleChoice",
  4: "pages.bankPool.multipleChoices",
  5: "pages.bankPool.essay",
  6: "pages.bankPool.connecting",
  7: "pages.bankPool.correctIncorrect",
  8: "pages.bankPool.imageChoice",
  9: "pages.bankPool.multipleImageChoice",
};

export const ExamQuestionSummaryCard = ({ examId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetExamQuestionSummary(examId);

  if (!examId) return null;

  const labelForType = (internalId: number, fallback: string) => {
    const key = questionTypeLabelKey[internalId];
    return key ? t(key) : fallback;
  };

  const hasParticipantBreakdown =
    !!data &&
    (data.participantBreakdown.length > 0 || data.participantRandomQuestions > 0);

  return (
    <Card
      size="small"
      style={{ marginBottom: 16, boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 5px" }}
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* What a participant will actually answer (based on group settings) */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 24,
            }}
          >
            <Statistic
              title={t("pages.examsManagment.examQuestionsTab.participantTotalQuestions")}
              value={data?.participantTotalQuestions ?? 0}
              prefix={<Users size={18} />}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              {hasParticipantBreakdown ? (
                <>
                  {data!.participantBreakdown.map((item) => (
                    <Tag
                      key={item.internalId}
                      color="blue"
                      style={{ margin: 0, padding: "4px 10px" }}
                    >
                      {labelForType(item.internalId, item.name)}:{" "}
                      <Typography.Text strong>{item.count}</Typography.Text>
                    </Tag>
                  ))}
                  {data!.participantRandomQuestions > 0 && (
                    <Tooltip
                      title={t("pages.examsManagment.examQuestionsTab.randomQuestionsHint")}
                    >
                      <Tag
                        icon={<Shuffle size={12} style={{ marginRight: 4 }} />}
                        color="purple"
                        style={{ margin: 0, padding: "4px 10px" }}
                      >
                        {t("pages.examsManagment.examQuestionsTab.randomQuestions")}:{" "}
                        <Typography.Text strong>
                          {data!.participantRandomQuestions}
                        </Typography.Text>
                      </Tag>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Typography.Text type="secondary">
                  {t("pages.examsManagment.examQuestionsTab.noQuestionsYet")}
                </Typography.Text>
              )}
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Full question pool added to the exam groups */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 24,
            }}
          >
            <Statistic
              title={t("pages.examsManagment.examQuestionsTab.totalQuestions")}
              value={data?.totalQuestions ?? 0}
              prefix={<FileQuestion size={18} />}
              valueStyle={{ fontSize: 18 }}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              {data && data.breakdown.length > 0 ? (
                data.breakdown.map((item) => (
                  <Tag key={item.internalId} style={{ margin: 0, padding: "2px 10px" }}>
                    {labelForType(item.internalId, item.name)}:{" "}
                    <Typography.Text strong>{item.count}</Typography.Text>
                  </Tag>
                ))
              ) : (
                <Typography.Text type="secondary">
                  {t("pages.examsManagment.examQuestionsTab.noQuestionsYet")}
                </Typography.Text>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
